import { z } from 'zod';
import {
  CURRENCIES,
  DIGEST_FREQUENCIES,
  EQUIPMENT_TYPES,
  INTERVAL_PRESETS,
  PAUSE_REASONS,
  PAYMENT_METHODS,
  TRADE_TYPES,
} from './constants';
import {
  CGK_CUSTOMER_TYPES,
  CGK_TRADES,
  PASSWORD_MIN_LENGTH,
} from './cgk-onboarding';

const passwordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `Password must be at least ${PASSWORD_MIN_LENGTH} characters`);

const cgkPriorityEnum = z.enum(['search', 'content', 'quotes', 'proof', 'social']);

export const signupSchema = z.object({
  /** Optional — collected in onboarding if omitted. */
  name: z.string().max(120).optional().or(z.literal('')),
  email: z.string().email('Valid email required'),
  password: passwordSchema,
  /** Optional — Spec collects business name in onboarding step 1. */
  business_name: z.string().max(120).optional().or(z.literal('')),
  timezone: z.string().min(1, 'Timezone is required'),
  accept_terms: z.boolean().refine((v) => v === true, {
    message: 'You must agree to the Terms and Privacy Policy',
  }),
  turnstile_token: z.string().optional(),
  /** Honeypot — must stay empty. */
  website: z.string().max(200).optional(),
  /** Website URL carried from landing / preview (not the honeypot). */
  website_url: z.string().max(500).optional(),
  referral_code: z.string().max(64).optional(),
  referral_src: z.string().max(32).optional(),
});

export const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
  turnstile_token: z.string().optional(),
  website: z.string().max(200).optional(),
});

export const forgotPasswordSchema = z.object({
  email: z.string().email(),
  turnstile_token: z.string().optional(),
  website: z.string().max(200).optional(),
});

export const resetPasswordSchema = z.object({
  token: z.string().min(1),
  password: passwordSchema,
});

export const verifyEmailCodeSchema = z.object({
  code: z.string().regex(/^\d{6}$/, 'Enter the 6-digit code'),
  email: z.string().email().optional(),
});

export const verifyEmailTokenSchema = z.object({
  token: z.string().min(1),
});

export const resendVerificationSchema = z.object({
  email: z.string().email().optional(),
});

/** Legacy Renewal Drive onboarding (flag-gated). */
export const onboardingStep1Schema = z.object({
  trade_type: z.enum(TRADE_TYPES),
  default_interval: z.enum(INTERVAL_PRESETS),
  reminder_offsets: z.array(z.number().int()).min(1),
});

const phoneUsOptional = z
  .string()
  .max(20)
  .optional()
  .or(z.literal(''))
  .refine((v) => !v || v.replace(/\D/g, '').length === 10, {
    message: 'Enter a 10-digit US phone number',
  });

export const cgkOnboardingSaveSchema = z.object({
  step: z.number().int().min(1).max(6),
  business_name: z.string().max(120).optional(),
  trade: z.enum(CGK_TRADES).optional().nullable(),
  trade_other: z.string().max(80).optional(),
  phone: phoneUsOptional,
  first_name: z.string().max(80).optional(),
  priorities: z.array(cgkPriorityEnum).max(5).optional(),
  website_url: z.string().max(500).optional().nullable(),
  no_website: z.boolean().optional(),
  state: z.string().max(2).optional().nullable(),
  city: z.string().max(120).optional().nullable(),
  service_area_text: z.string().max(500).optional(),
  customer_type: z.enum(CGK_CUSTOMER_TYPES).optional().nullable(),
  services: z.array(z.string().max(80)).max(12).optional(),
  differentiator: z.string().max(200).optional().nullable(),
  years_in_business: z.number().int().min(0).max(120).optional().nullable(),
  complete: z.boolean().optional(),
});

export const contactSchema = z.object({
  name: z.string().min(1).max(120),
  email: z.string().email().optional().or(z.literal('')),
  phone: z.string().max(20).optional(),
  company: z.string().max(120).optional(),
  address: z.string().max(500).optional(),
  notes: z.string().max(5000).optional(),
  tags: z.array(z.string()).optional(),
});

export const agreementSchema = z
  .object({
    contact_id: z.string().uuid().optional(),
    customer_name: z.string().min(1).max(120).optional(),
    customer_email: z.string().email().optional(),
    customer_phone: z.string().max(20).optional(),
    customer_address: z.string().max(500).optional(),
    plan_name: z.string().min(1).max(120),
    amount: z.number().min(0),
    currency: z.enum(CURRENCIES),
    start_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    end_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    interval_months: z.number().int().min(1).max(36),
    external_ref: z.string().max(120).optional(),
    notes: z.string().max(5000).optional(),
    payment_link_url: z.string().url().optional().or(z.literal('')),
    reminder_offsets: z.array(z.number().int()).optional(),
    status: z.enum(['draft', 'active', 'paused']).optional(),
    payment_link_enabled: z.boolean().optional(),
    auto_renew: z.boolean().optional(),
    technician_id: z.string().uuid().optional(),
  })
  .refine((d) => d.end_date > d.start_date, {
    message: 'End date must be after start date',
    path: ['end_date'],
  });

export const markRenewedSchema = z.object({
  new_end_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  payment_method: z.enum(PAYMENT_METHODS),
  amount_received: z.number().min(0).optional(),
  note: z.string().max(2000).optional(),
});

export const pauseSchema = z.object({
  reason: z.enum(PAUSE_REASONS),
});

export const settingsSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  timezone: z.string().optional(),
  country: z.string().length(2).optional(),
  from_name: z.string().max(120).optional(),
  reply_to_email: z.string().email().optional(),
  business_phone: z.string().max(20).optional(),
  owner_digest_frequency: z.enum(DIGEST_FREQUENCIES).optional(),
});

export const reminderSettingsSchema = z.object({
  default_reminder_offsets: z.array(z.number().int()).min(1),
  send_window_start: z.number().int().min(0).max(23).optional(),
  send_window_end: z.number().int().min(1).max(23).optional(),
});

export const emailTemplateSchema = z.object({
  template_key: z.string().min(1),
  subject: z.string().min(1).max(200),
  body_html: z.string().min(1),
  body_text: z.string().optional(),
});

export const bulkActionSchema = z.object({
  obligation_ids: z.array(z.string().uuid()).min(1),
  action: z.enum(['send_reminder', 'pause', 'archive']),
  pause_reason: z.enum(PAUSE_REASONS).optional(),
});

export type SignupInput = z.infer<typeof signupSchema>;
export type LoginInput = z.infer<typeof loginSchema>;
export type ContactInput = z.infer<typeof contactSchema>;
export type AgreementInput = z.infer<typeof agreementSchema>;
export type MarkRenewedInput = z.infer<typeof markRenewedSchema>;

export const agreementUpdateSchema = z.object({
  contact_id: z.string().uuid().optional(),
  plan_name: z.string().min(1).max(120).optional(),
  amount: z.number().min(0).optional(),
  currency: z.enum(CURRENCIES).optional(),
  start_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  end_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  interval_months: z.number().int().min(1).max(36).optional(),
  external_ref: z.string().max(120).optional(),
  notes: z.string().max(5000).optional(),
  payment_link_url: z.string().url().optional().or(z.literal('')),
  reminder_offsets: z.array(z.number().int()).optional(),
  payment_link_enabled: z.boolean().optional(),
  auto_renew: z.boolean().optional(),
  technician_id: z.string().uuid().nullable().optional(),
});

export const technicianSchema = z.object({
  name: z.string().min(1).max(120),
  external_ref: z.string().max(120).optional(),
  active: z.boolean().optional(),
});

export const technicianUpdateSchema = technicianSchema.partial();

export const paymentSettingsSchema = z.object({
  default_payment_links_enabled: z.boolean().optional(),
  static_payment_links: z
    .array(
      z.object({
        id: z.string().min(1).max(40),
        provider: z.string().max(20),
        label: z.string().min(1).max(80),
        url: z.string().url(),
      }),
    )
    .max(6)
    .optional(),
  default_static_payment_link_id: z.string().max(40).nullable().optional(),
});

export const opportunityStageSchema = z.object({
  stage: z.enum(['identified', 'offered', 'converted', 'lost']),
  offered_plan: z.string().max(120).optional(),
  estimated_value: z.number().min(0).optional(),
});

export const convertOpportunitySchema = agreementSchema;

export const equipmentSchema = z.object({
  type: z.enum(EQUIPMENT_TYPES),
  name: z.string().min(1).max(120),
  install_year: z.number().int().min(1950).max(new Date().getFullYear()),
  expected_life_years: z.number().int().min(1).max(50).optional(),
  obligation_id: z.string().optional(),
  notes: z.string().max(2000).optional(),
});

export const equipmentUpdateSchema = equipmentSchema.partial();

export const upgradeQuoteSchema = z.object({
  equipment_ids: z.array(z.string()).optional(),
  notes: z.string().max(2000).optional(),
});

export const upgradeConvertSchema = z.object({
  amount: z.number().min(0),
  equipment_ids: z.array(z.string()).optional(),
  notes: z.string().max(2000).optional(),
});

export const contactUpdateSchema = contactSchema.partial();

export const csvDedupeModeSchema = z.enum([
  'create_only',
  'update_by_email',
  'update_by_external_ref',
  'skip_existing',
]);

export const csvImportPreviewSchema = z.object({
  csv: z.string().min(1),
  mapping: z.record(z.string(), z.string().nullable()).optional(),
});

export const csvImportSchema = z.object({
  csv: z.string().min(1),
  mapping: z.record(z.string(), z.string().nullable()).optional(),
  dedupe_mode: csvDedupeModeSchema.optional(),
});

export const integrationFieldMappingSchema = z.object({
  field_mapping: z.record(z.string(), z.string()),
});

export const supportMessageSchema = z.object({
  message: z.string().min(1, 'Message is required').max(5000),
  name: z.string().max(120).optional(),
  email: z.string().email('Valid email required').optional(),
  page_path: z.string().max(500).optional(),
  context: z.string().max(120).optional(),
  turnstile_token: z.string().optional(),
  /** Honeypot — must stay empty. */
  website: z.string().max(200).optional(),
});

export const feedbackPromptDismissSchema = z.object({
  trigger: z.enum(['onboarding', 'import', 'checkin']),
});
